import React, { useContext } from 'react'
import { useSelector, shallowEqual } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography, Button, useTheme } from '@material-ui/core'
import { AttachMoney, ArrowForwardIos, VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons'

import AreaChartPrice from '~/components/Chart/AreaChart'
import DrawerAlertPrice from '~/components/Ecommerce/DrawerAlertPrice'

import track from '~/track'
import { useLiterals } from '~/components/Literals'
import { selectChart } from '~/selectors'

import { OnlineStoreContext } from '.'

const useStyles = makeStyles((theme) => ({
  areaProduct: {
    gridColumn: 'span 1',
    backgroundColor: '#FFF',
    padding: 16,
    [theme.breakpoints.up('md')]: {
      borderRight: '2px solid #EEEBEB',
    },
    [theme.breakpoints.down('sm')]: {
      borderBottom: '2px solid #EEEBEB',
    },

    gap: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& img': { mixBlendMode: 'multiply' },
    '& p': {
      width: '100%',
      maxWidth: 460,
    },
    '& > div': {
      width: '100%',
      maxWidth: 460,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  productDescription: {
    padding: 0,
    textTransform: 'none',
    textDecoration: 'underline',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
    '&:focus': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  cardGraph: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    gap: 16,
    borderTop: '2px solid #EEEBEB',
    borderBottom: '2px solid #EEEBEB',
    paddingBlock: 16,
  },
  loadChart: {
    width: '100%',
    position: 'absolute',
    inset: 0,
    zIndex: 100,
    display: 'none',
    '& p': { textAlign: 'center', fontSize: 20 },
  },
}))

export function ProductSelected() {
  const { getLiteral } = useLiterals()
  const { currentProduct, openSheetPriceMonitoring, showMoreDescriptionProduct, dispatch } =
    useContext(OnlineStoreContext)

  const theme = useTheme()
  const classes = useStyles()
  const descriptionLength = currentProduct?.description?.length || 0

  const [chart] = useSelector(selectChart, shallowEqual)

  const { data, load, period } = chart
  const dataChart = data.map(({ date, lowestPrice }) => ({
    date: new Date(date),
    value: lowestPrice,
  }))

  const handleOpen = (key, product) => {
    key === 'variantPrice'
      ? track('verPrecoOnline', { category: 'produto', name: product?.name })
      : track('alertaPrecoOnline', {
          category: 'produto',
          name: product?.name,
        })

    dispatch({ openSheetPriceMonitoring: true })
  }

  const handleClose = () => dispatch({ openSheetPriceMonitoring: false })

  return (
    <>
      <Box className={classes.areaProduct}>
        <Box
          component="img"
          src={currentProduct?.imageurl}
          alt={currentProduct?.name}
          sx={{
            width: { xs: 130, md: 200 },
            height: { xs: 130, md: 200 },
          }}
        />

        <Typography
          variant="body1"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: showMoreDescriptionProduct ? 'unset' : '2',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {currentProduct?.description}
        </Typography>

        {descriptionLength >= 120 && (
          <Box onClick={() => dispatch({ showMoreDescriptionProduct: !showMoreDescriptionProduct })}>
            <Button variant="text" className={classes.productDescription}>
              {showMoreDescriptionProduct ? getLiteral('2476') : getLiteral('1524')}
            </Button>
            <ArrowForwardIos style={{ fontSize: 16, color: theme.palette.primary.main }} />
          </Box>
        )}

        {dataChart.length > 0 && (
          <>
            <Box className={classes.cardGraph} onClick={() => handleOpen('variantPrice', currentProduct)}>
              <Box
                className={classes.loadChart}
                {...(load && {
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#FFFFFF60',
                    backdropFilter: 'blur(3px)',
                  },
                })}
              >
                <Typography variant="body1">{getLiteral('1503')}...</Typography>
              </Box>

              <AreaChartPrice
                onlyGraphich
                data={dataChart}
                color={theme.palette.primary.main}
                width="100%"
                height={100}
              />

              <Typography style={{ textDecoration: 'underline', width: 'auto' }}>{getLiteral('2535')}</Typography>
            </Box>

            <Button
              variant="contained"
              disableElevation
              startIcon={<AttachMoney color="primary" />}
              onClick={() => handleOpen('alertPrice', currentProduct)}
              style={{ textTransform: 'none' }}
            >
              {getLiteral('2542')}
            </Button>
          </>
        )}
      </Box>

      <DrawerAlertPrice
        direction="left"
        product={currentProduct}
        open={openSheetPriceMonitoring}
        color={theme.palette.primary.main}
        onClose={handleClose}
        periodChart={period}
        loadChart={load}
        dataChart={dataChart}
      />
    </>
  )
}
